// ─── « Mon forfait » — côté UTILISATEUR (Core) ────────────────────
// Montre le forfait en cours, ce qu'il apporte, et permet de DEMANDER un autre
// forfait : l'utilisateur ne peut pas s'attribuer un plan lui-même (règles
// Firestore) — la demande est traitée dans MoP Admin par le commerce.
//
// Les NOMS, PRIX et LIMITES viennent de `config/plans` (cf. plansConfig) : ils
// sont pilotés par l'administration, jamais figés dans le code.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking, Platform } from 'react-native';
import { doc, onSnapshot, setDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from '../auth/AuthContext';
import { useApp } from '../app/AppContext';
import { useFonctions } from './FonctionsContext';
import { PLANS, PLANS_PAYANTS, aDemandeForfait } from './plans';
import { PALIERS } from './fonctionnalites';
import { jetonsDuPlan } from './jetons';
import { ecouterPlans, nomPlan } from './plansConfig';
import CartePlan from './CartePlan';
import { confirmerChoix } from '../services/confirm';
import { creerSessionPaiement, annulerAbonnement } from './paiementService';
import { achatsNatifsDisponibles, obtenirOfferings, acheterPackage, gererAbonnementNatif } from './achatNatifService';

// Sur iOS/Android (binaire migré), l'achat passe par le store — jamais par un
// lien externe Stripe (politique Apple 3.1.1 / Google Play Billing). Le web
// garde Stripe : les règles des stores ne s'appliquent qu'à ce qu'ils distribuent.
const NATIF = Platform.OS !== 'web' && achatsNatifsDisponibles();

export default function SectionMonForfait() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const app = useApp();
  const { catalogue } = useFonctions();
  const [cfg, setCfg] = useState({ plans: {}, paliers: {} });   // config/fonctionnalites
  const [plans, setPlans] = useState({});                        // config/plans
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => onSnapshot(doc(db, 'config', 'fonctionnalites'),
    (snap) => { const d = snap.exists() ? snap.data() : {}; setCfg({ plans: d.plans || {}, paliers: d.paliers || {} }); },
    () => {}), []);
  useEffect(() => ecouterPlans(setPlans), []);

  const planActuel = user?.plan || 'gratuit';
  const enAttente = aDemandeForfait(user) ? user.planDemande : null;

  // Nombre de fonctions apportées par un plan (au-delà du socle, toujours inclus).
  const ordrePaliers = PALIERS.map(x => x.id);
  const nbFonctions = (plan) => {
    const palier = cfg.plans?.[plan]?.palierInclus;
    if (!palier) return null;
    const max = ordrePaliers.indexOf(palier);
    return (catalogue.racines || []).filter(d => {
      const p = cfg.paliers?.[d.key] || d.palier || 'basic';
      return p !== 'socle' && ordrePaliers.indexOf(p) <= max;
    }).length;
  };

  const demander = (plan) => {
    if (!user?.uid || plan === planActuel) return;
    confirmerChoix(t('forfait.demanderTitre'), t('forfait.demanderMsg', { plan: nomPlan(plans, plan) }), [
      { text: t('forfait.demander'), onPress: () => setDoc(doc(db, 'users', user.uid),
        { planDemande: plan, planDemandeLe: new Date().toISOString() }, { merge: true }).catch(() => {}) },
      { text: t('commun.annuler'), style: 'cancel' },
    ]);
  };
  const annulerDemande = () => setDoc(doc(db, 'users', user.uid),
    { planDemande: deleteField(), planDemandeLe: deleteField() }, { merge: true }).catch(() => {});

  // Circuit complet automatique (Cédric, 19/08) : un forfait PAYANT ne passe
  // jamais par `planDemande` — la validation (Stripe Checkout ou achat natif)
  // déclenche le webhook côté serveur, qui applique le forfait tout seul.
  //
  // Achat natif (22/08) : l'offre RevenueCat porte l'IDENTIFIANT DU PLAN
  // (`plan`), même convention que l'entitlement — voir `achatNatifService.js`
  // et la note de configuration RevenueCat dans COWORK.md.
  const payerNatif = async (plan) => {
    const offerings = await obtenirOfferings();
    const toutes = offerings?.all || {};
    const offre = toutes[plan] || (Object.keys(toutes).length === 1 ? Object.values(toutes)[0] : null);
    const pkg = offre?.availablePackages?.[0];
    if (!pkg) throw new Error('Offre indisponible');
    await acheterPackage(pkg);
  };
  const payer = async (plan) => {
    if (busy || !user?.uid) return;
    setBusy(true); setErr('');
    try {
      if (NATIF) {
        await payerNatif(plan);
      } else {
        const { url } = await creerSessionPaiement(plan, app.scheme);
        if (url) await Linking.openURL(url);
        else throw new Error('URL manquante');
      }
    } catch (e) {
      // Annulation volontaire (croix sur la feuille de paiement du store) :
      // pas une erreur, rien à afficher.
      if (!e?.userCancelled) setErr(t('forfait.erreurPaiement'));
    }
    setBusy(false);
  };
  // Résilier un abonnement NATIF ne peut pas passer par notre code (Apple/
  // Google imposent leur propre écran de gestion) — pas de confirmation à
  // afficher ici, le store gère déjà sa propre confirmation.
  const passerGratuit = () => {
    if (busy || !user?.uid) return;
    if (NATIF) { gererAbonnementNatif(); return; }
    confirmerChoix(t('forfait.passerGratuitTitre'), t('forfait.passerGratuitMsg'), [
      { text: t('forfait.passerGratuit'), style: 'destructive', onPress: async () => {
        setBusy(true); setErr('');
        try { await annulerAbonnement(); }
        catch (e) { setErr(e?.message || String(e)); }
        setBusy(false);
      } },
      { text: t('commun.annuler'), style: 'cancel' },
    ]);
  };

  // Un plan hérité, absent du catalogue courant, reste affiché : sinon
  // l'utilisateur ne verrait AUCUN forfait sélectionné. `horscat` : attribué
  // par l'administration seulement (cf. plans.js) — ne JAMAIS le proposer à la
  // demande, sauf à celui qui l'a déjà (pour qu'il voie son propre statut).
  const liste = (PLANS.includes(planActuel) ? PLANS : [...PLANS, planActuel])
    .filter(p => p !== 'horscat' || p === planActuel)
    .sort((a, b) => (plans[a]?.ordre ?? 99) - (plans[b]?.ordre ?? 99));

  return (
    <View>
      <Text style={s.aide}>{t('forfait.monForfaitAide')}</Text>

      {/* Demande en cours : bandeau + annulation. */}
      {!!enAttente && (
        <TouchableOpacity style={s.demande} onPress={annulerDemande} activeOpacity={0.7}>
          <Text style={s.demandeTxt}>⏳ {t('forfait.demandeEnCours', { plan: nomPlan(plans, enAttente) })}</Text>
          <Text style={s.demandeAnnul}>{t('commun.annuler')}</Text>
        </TouchableOpacity>
      )}

      {!!err && (
        <TouchableOpacity style={s.err} onPress={() => setErr('')}>
          <Text style={s.errTxt}>⚠️ {err}</Text>
        </TouchableOpacity>
      )}

      {liste.map(p => {
        const estActuel = p === planActuel;
        const payant = PLANS_PAYANTS.includes(p);
        const planActuelPayant = PLANS_PAYANTS.includes(planActuel);
        let libelleAction, action, actionActive = false;
        if (estActuel && payant) {
          libelleAction = t('forfait.passerGratuit'); action = passerGratuit; actionActive = true;
        } else if (!estActuel && payant) {
          libelleAction = t('forfait.payer', { prix: plans[p]?.prix?.mensuel ?? '?' }); action = () => payer(p);
        } else if (!estActuel && p === 'gratuit' && planActuelPayant) {
          libelleAction = t('forfait.passerGratuit'); action = passerGratuit;
        } else {
          action = () => demander(p);
        }
        return (
          <CartePlan key={p} plan={plans[p]} nom={nomPlan(plans, p)}
            jetons={jetonsDuPlan(cfg, p)} nbFonctions={nbFonctions(p)}
            actuel={estActuel} enAttente={enAttente === p} bloque={!!enAttente && enAttente !== p}
            libelleAction={libelleAction} actionActive={actionActive}
            onDemander={action} />
        );
      })}
      <Text style={s.aide}>{t('forfait.validationAide')}</Text>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginBottom: 10 },
  demande: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.champ,
             borderRadius: 11, borderWidth: 1, borderColor: UI.accent, padding: 11, marginBottom: 10 },
  demandeTxt: { flex: 1, fontSize: 13, color: UI.ink, fontWeight: '700' },
  demandeAnnul: { fontSize: 12, color: UI.accent, fontWeight: '800' },
  err:    { backgroundColor: UI.dangerTint, borderRadius: 10, borderWidth: 1, borderColor: UI.danger,
            padding: 11, marginBottom: 10 },
  errTxt: { color: UI.danger, fontSize: 12.5, fontWeight: '600' },
});
